import { Injectable } from '@angular/core';
import { environment } from '@env/environment';
import { localStorageSupported, sessionStorageSupported } from '@shared/utilities';
import { CookieService } from 'ngx-cookie-service';

import { storageStore, StorageType } from './_models';

@Injectable({
	providedIn: 'root'
})
export class StorageService {
	private inMemoryStorage = {};
	private sessionStorageSupported = sessionStorageSupported();
	private localStorageSupported = localStorageSupported();
	private domain = environment.cookieDomain;

	constructor(private cookieService: CookieService) {
		this.getCookieDomain();
	}

	/**
	 * This is an interim solution to ensure we are storing and reading Cookies from the correct domain
	 * and allows us to continue support zone5cloud domain while we transition to new specialized domains.
	 * Only relevant for existing sessions and any actions that would trigger a re-auth (logout, 401 etc)
	 * will redirect user to auth via new domain essentially preventing new sessions from being started
	 * on old domain.
	 */
	private getCookieDomain(): void {
		const domain = window.location.hostname;

		this.domain =
			domain.includes(environment.cookieDomain) || domain.includes('localhost') ? environment.cookieDomain : window.location.hostname;
	}

	/**
	 * Get single item from storage. Will return a string unless item is missing.
	 *
	 * @param storageType StorageType to access
	 * @param key Key to access
	 * @param store Prefix to key
	 */
	public getItem(storageType: StorageType, key: string, store = storageStore): any {
		const storeKey = store ? `${store}.${key}` : key;

		switch (this.validateStorageType(storageType)) {
			case StorageType.cookie:
				try {
					return JSON.parse(this.cookieService.get(storeKey));
				} catch (e) {
					return null;
				}
			case StorageType.sessionStorage:
				try {
					return JSON.parse(sessionStorage.getItem(storeKey));
				} catch (e) {
					return null;
				}
			case StorageType.localStorage:
				try {
					return JSON.parse(localStorage.getItem(storeKey));
				} catch (e) {
					return null;
				}
			case StorageType.inMemory:
				return this.inMemoryStorage[storeKey] || null;
			default:
				return null;
		}
	}

	/**
	 * Store single item.
	 *
	 * @param storageType StorageType to access
	 * @param key Key to store against
	 * @param value Value to store
	 * @param store Prefix to key
	 * @param expire Cookie expire
	 * @param path Cookie path
	 */
	public setItem(
		storageType: StorageType,
		key: string,
		value: any,
		store = storageStore,
		expire = 1,
		path = '/'
	): void {
		const storeKey = store ? `${store}.${key}` : key;

		switch (this.validateStorageType(storageType)) {
			case StorageType.cookie:
				this.cookieService.set(storeKey, JSON.stringify(value), expire, path, this.domain, true, 'Strict');
				break;
			case StorageType.sessionStorage:
				sessionStorage.setItem(storeKey, JSON.stringify(value));
				break;
			case StorageType.localStorage:
				localStorage.setItem(storeKey, JSON.stringify(value));
				break;
			case StorageType.inMemory:
				this.inMemoryStorage[storeKey] = value;
				break;
		}
	}

	/**
	 * Remove single item from storage.
	 *
	 * @param storageType StorageType to access
	 * @param store Store, used a prefix for key
	 * @param key Value key
	 * @param path Cookie path
	 */
	public removeItem(storageType: StorageType, key: string, store = storageStore, path = '/'): void {
		const storeKey = store ? `${store}.${key}` : key;

		switch (this.validateStorageType(storageType)) {
			case StorageType.cookie:
				this.cookieService.delete(storeKey, path, this.domain);
				// also delete from parent domain
				this.cookieService.delete(storeKey, path, this.domain.replace('admin.', ''));
				break;
			case StorageType.sessionStorage:
				sessionStorage.removeItem(storeKey);
				break;
			case StorageType.localStorage:
				localStorage.removeItem(storeKey);
				break;
			case StorageType.inMemory:
				delete this.inMemoryStorage[storeKey];
				break;
		}
	}

	/**
	 * Blow up storage, will clear provided storage option
	 * @param storageType cookie, local or session storage
	 * @param path Optional path, only affects Cookies
	 */
	public removeAll(storageType: StorageType, path = '/'): void {
		switch (storageType) {
			case StorageType.cookie:
				this.cookieService.deleteAll(path, this.domain);
				// also delete from parent domain
				this.cookieService.deleteAll(path, this.domain.replace('admin.', ''));
				break;
			case StorageType.sessionStorage:
				if (this.sessionStorageSupported) {
					sessionStorage.clear();
				}
				break;
			case StorageType.localStorage:
				if (this.localStorageSupported) {
					localStorage.clear();
				}
				break;
			case StorageType.inMemory:
				this.inMemoryStorage = {};
				break;
		}
	}

	public removeStore(storageType: StorageType, store = storageStore, path = '/', domain = this.domain): void {
		switch (storageType) {
			case StorageType.cookie:
				Object.keys(this.cookieService.getAll())
					.filter((key) => key.startsWith(store))
					.forEach((key) => {
						this.cookieService.delete(key, path, domain);
						// also delete from parent domain
						this.cookieService.delete(key, path, domain.replace('admin.', ''));
					});
				break;
			case StorageType.sessionStorage:
				if (this.sessionStorageSupported) {
					Object.keys(sessionStorage)
						.filter((key) => key.startsWith(store))
						.forEach((key) => sessionStorage.removeItem(key));
				}
				break;
			case StorageType.localStorage:
				if (this.localStorageSupported) {
					Object.keys(localStorage)
						.filter((key) => key.startsWith(store))
						.forEach((key) => localStorage.removeItem(key));
				}
				break;
			case StorageType.inMemory:
				Object.keys(this.inMemoryStorage)
					.filter((key) => key.startsWith(store))
					.forEach((key) => delete this.inMemoryStorage[key]);
				break;
		}
	}

	/**
	 * Override storage type if not supported and return replacement type.
	 *
	 * @param storageType Storage type
	 */
	private validateStorageType(storageType: StorageType): StorageType {
		if (storageType === StorageType.sessionStorage && !this.sessionStorageSupported) {
			return StorageType.inMemory;
		}

		if (storageType === StorageType.localStorage && !this.localStorageSupported) {
			return StorageType.inMemory;
		}

		return storageType;
	}
}
